import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  defer,
  EMPTY,
  firstValueFrom,
  Observable,
  of,
} from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { CurrentUserInfo } from './user-information.model';
import { UserProfileClient } from '../../openapi/identity';

@Injectable({
  providedIn: 'root',
})
export class UserInformationService {
  private readonly currentUser$: BehaviorSubject<
    CurrentUserInfo | null | undefined
  > = new BehaviorSubject<CurrentUserInfo | null | undefined>(null);

  public readonly userPermissions$: Observable<string[]> = defer(() =>
    this.authService.hasValidAccessToken() ? this.getCurrentUser() : of(null),
  ).pipe(
    map((userInfo) => userInfo?.permissions ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private isUserLoading = false;

  constructor(
    private readonly userProfileClient: UserProfileClient,
    private readonly authService: AuthService,
  ) {}

  public getCurrentUser(): Observable<CurrentUserInfo | null | undefined> {
    if (!this.currentUser$.value && !this.isUserLoading) {
      this.loadCurrentUser().then();
    }

    return this.currentUser$.asObservable();
  }

  public loadCurrentUser(): Promise<void> {
    this.isUserLoading = true;

    return firstValueFrom(
      this.userProfileClient.userProfileGet().pipe(
        finalize(() => (this.isUserLoading = false)),
        map((user: CurrentUserInfo) => {
          this.isUserLoading = false;
          this.currentUser$.next(user);
        }),
        catchError(() => {
          this.currentUser$.next(null);
          return EMPTY;
        }),
      ),
    );
  }
}
